import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import async from 'async';
import pluralize from 'pluralize';
import { SOLR_DELAY } from '../../../../constants';
import { getUsers } from '../../../actions/userActions';
import { getSigns } from '../../../actions/signActions';
import { loadSequences } from '../../../actions/sequenceActions';
import { loadSchedules } from '../../../actions/scheduleActions';
import { loadPlaylists } from '../../../actions/playlistActions';
import { loadCampaigns } from '../../../actions/campaignActions';
import { loadAllAssignments } from '../../../actions/assignmentActions';
import {
  getUrlRuleset,
  addUrlRule,
  updateUrlRule,
  deleteUrlRule,
  getInUse,
  select,
  unselectAll,
} from '../../../actions/urlRulesetActions';
import { addNotification } from '../../../actions/notificationActions';
import Spinner from '../../../components/Spinner/Spinner';
import Confirm from '../../../components/Confirm/Confirm';
import UrlRulesTable from '../../../components/UrlRulesets/UrlRulesTable';
import AddUrlRuleDialog from '../../../components/UrlRulesets/AddUrlRuleDialog';
import EditUrlRuleDialog from '../../../components/UrlRulesets/EditUrlRuleDialog';
import Used from '../../../components/Used/Used';
import Header from '../../../components/Header/Header';
import ErrorPage from '../ErrorPage/ErrorPage';
import errorMessage from '../../../utils/errorMessage';
import URLRulesetActions from './URLRulesetActions';

export class UrlRulesetPage extends Component {
  state = {
    showAddDialog: false,
    showEditDialog: false,
  };

  componentDidMount() {
    this.loadData();
    document.addEventListener('visibilitychange', this.loadData);
  }

  componentWillUnmount() {
    const { unselectAll } = this.props;

    unselectAll();
    document.removeEventListener('visibilitychange', this.loadData);
  }

  loadData = () => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    const {
      match: {
        params: { id },
      },
      getSigns,
      loadSequences,
      loadSchedules,
      loadPlaylists,
      loadCampaigns,
      loadAllAssignments,
      getUsers,
      getUrlRuleset,
      getInUse,
    } = this.props;

    getSigns('?count=100000000');
    loadSequences('?count=100000000');
    loadSchedules('?count=100000000');
    loadPlaylists('?count=100000000');
    loadCampaigns('?count=100000000');
    loadAllAssignments();
    getUsers();
    getUrlRuleset(id);
    getInUse();
  };

  getSelectedItems = () => {
    const {
      urlRulesets: { selected },
    } = this.props;
    const items = this.getWhiteBlackList();

    return Object.keys(selected).map(index => items[index]);
  };

  getUsedInSigns = () => {
    const {
      match: {
        params: { id },
      },
      urlRulesets: { inUse },
    } = this.props;

    inUse.filter(item => item.urlRulesetId === +id).map(item => `${item.signId}-${item.published}`);
  };

  getWhiteBlackList = () => {
    const {
      urlRulesets: {
        urlRuleset: { whitelist, blacklist },
      },
    } = this.props;

    if (!whitelist) {
      return [];
    }

    const white = whitelist.map(item => {
      if (item.beginsWith === 'false') {
        item.beginsWith = false;
      }

      item.type = 'white';

      return item;
    });
    const black = blacklist.map(item => {
      item.type = 'black';

      return item;
    });

    return [...white, ...black];
  };

  add = urlRule => {
    const {
      match: {
        params: { id },
      },
      getUrlRuleset,
      addNotification,
    } = this.props;
    const { type, beginsWith, rule } = urlRule;

    return addUrlRule(id, type, beginsWith, rule).then(
      () => {
        addNotification({ type: 'success', text: 'URL Rule added.' });
        setTimeout(() => {
          getUrlRuleset(id);
        }, SOLR_DELAY);
      },
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  update = urlRule => {
    const {
      match: {
        params: { id },
      },
      getUrlRuleset,
      addNotification,
    } = this.props;
    const { type, beginsWith, rule } = urlRule;
    const ruleId = this.getSelectedItems()[0].id;

    return updateUrlRule(id, ruleId, type, beginsWith, rule).then(
      res => {
        if (res.id > -1) {
          addNotification({ type: 'success', text: 'URL Rule updated.' });
          setTimeout(() => {
            getUrlRuleset(id);
          }, SOLR_DELAY);
        } else {
          addNotification({ type: 'danger', text: 'Request failed.' });
        }
      },
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  delete = () => {
    const {
      match: {
        params: { id },
      },
    } = this.props;
    const items = this.getSelectedItems();

    async.filter(
      items,
      (item, callback) =>
        deleteUrlRule(id, item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      this.deleteCallback,
    );
  };

  deleteCallback = (err, results) => {
    const {
      match: {
        params: { id },
      },
      getUrlRuleset,
      addNotification,
      unselectAll,
    } = this.props;
    const items = this.getSelectedItems();
    let failedItems;
    let errorMsg = '';

    // check for errors
    if (items.length === results.length) {
      addNotification({
        type: 'success',
        text: `${pluralize('URL Rule', items.length, true)} deleted.`,
      });
    } else {
      // collect the failed request items
      failedItems = items.filter(item => !results.includes(item));
      failedItems.forEach(item => {
        errorMsg += (errorMsg ? ', ' : 'Failed to delete: ') + item.rule;
      });
      addNotification({ type: 'danger', text: errorMsg });
    }

    // if there was at least one successful request, update
    if (results.length) {
      unselectAll();
      setTimeout(() => {
        getUrlRuleset(id);
      }, SOLR_DELAY);
    }
  };

  successAddDialog = urlRule => {
    this.hideAddDialog();
    this.add(urlRule);
  };

  successEditDialog = urlRule => {
    this.hideEditDialog();
    this.update(urlRule);
  };

  hideAddDialog = () => this.setState({ showAddDialog: false });

  hideEditDialog = () => this.setState({ showEditDialog: false });

  handleClickAdd = () => this.setState({ showAddDialog: true });

  handleClickEdit = () => this.setState({ showEditDialog: true });

  handleClickDelete = () => {
    const confirm = this.refDelete;
    const items = this.getSelectedItems();
    const names = items.map((item, index) => (
      <li key={index}>
        {item.type}list: {item.rule}
      </li>
    ));
    const title = `Delete ${pluralize('URL Rule', items.length, true)}`;
    const body = (
      <div>
        <p>
          Are you sure you want to delete {pluralize('this', items.length)}{' '}
          {pluralize('URL Rule', items.length)}?
        </p>
        <ol>{names}</ol>
      </div>
    );

    confirm.show(title, body);
    confirm.onSuccess(this.delete);

    return { title, body };
  };

  handleClickTable = urlRules => {
    const { select } = this.props;

    select(urlRules);
  };

  handleDoubleClickTable = () => {
    const {
      auth: {
        user: {
          permissions: { Writer },
        },
      },
    } = this.props;

    if (Writer) {
      this.handleClickEdit();
    }
  };

  render() {
    const {
      auth,
      users: { users },
      urlRulesets: { urlRuleset, selected, loading, error },
      addNotification,
      signs: {
        signs: { signs },
      },
      sequences: {
        sequences: { sequences },
      },
      schedules: {
        schedules: { schedules },
      },
      playlists: {
        playlists: { playlists },
      },
      campaigns: {
        campaigns: { campaigns },
      },
      assignments: {
        allAssignments: { assignments },
      },
    } = this.props;
    const { showAddDialog, showEditDialog } = this.state;

    if (error) {
      return <ErrorPage error={error} />;
    }

    const items = this.getSelectedItems();
    const data = this.getWhiteBlackList();

    return (
      <div className="page-structure !bg-star-light min-h-screen">
        <Helmet title="URL Rules" />
        <Header overwrite={{ id: urlRuleset }} />

        <URLRulesetActions
          auth={auth}
          removables={items.length}
          getSelectedItems={this.getSelectedItems}
          handleClickDelete={this.handleClickDelete}
          handleClickAdd={this.handleClickAdd}
        />

        <div className="mt-8">
          <div className="page-content page-url-rulesets">
            <Spinner loading={loading}>
              <UrlRulesTable
                data={data}
                selected={selected}
                onClick={this.handleClickTable}
                onDoubleClick={this.handleDoubleClickTable}
              />
              <Used
                type="asset"
                item={{ usedInSigns: this.getUsedInSigns() }}
                signs={signs}
                sequences={sequences}
                schedules={schedules}
                playlists={playlists}
                campaigns={campaigns}
                assignments={assignments}
                auth={auth}
                users={users}
              />
            </Spinner>
          </div>
        </div>
        <AddUrlRuleDialog
          show={showAddDialog}
          onHide={this.hideAddDialog}
          onSuccess={this.successAddDialog}
          addNotification={addNotification}
        />
        <EditUrlRuleDialog
          show={showEditDialog}
          onHide={this.hideEditDialog}
          onSuccess={this.successEditDialog}
          urlRule={items[0]}
          addNotification={addNotification}
        />
        <Confirm
          ref={ref => {
            this.refDelete = ref;
          }}
        />
      </div>
    );
  }
}

UrlRulesetPage.propTypes = {
  match: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  users: PropTypes.object.isRequired,
  signs: PropTypes.object.isRequired,
  sequences: PropTypes.object.isRequired,
  schedules: PropTypes.object.isRequired,
  playlists: PropTypes.object.isRequired,
  campaigns: PropTypes.object.isRequired,
  assignments: PropTypes.object.isRequired,
  urlRulesets: PropTypes.object.isRequired,
  getUrlRuleset: PropTypes.func.isRequired,
  getUsers: PropTypes.func.isRequired,
  getSigns: PropTypes.func.isRequired,
  loadSequences: PropTypes.func.isRequired,
  loadSchedules: PropTypes.func.isRequired,
  loadPlaylists: PropTypes.func.isRequired,
  loadCampaigns: PropTypes.func.isRequired,
  loadAllAssignments: PropTypes.func.isRequired,
  getInUse: PropTypes.func.isRequired,
  select: PropTypes.func.isRequired,
  unselectAll: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
  users: state.users,
  signs: state.signs,
  sequences: state.sequences,
  schedules: state.schedules,
  playlists: state.playlists,
  campaigns: state.campaigns,
  assignments: state.assignments,
  urlRulesets: state.urlRulesets,
});
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getUsers,
      getSigns,
      loadSequences,
      loadSchedules,
      loadPlaylists,
      loadCampaigns,
      loadAllAssignments,
      getUrlRuleset,
      getInUse,
      select,
      unselectAll,
      addNotification,
    },
    dispatch,
  );

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UrlRulesetPage));
